import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { ArrowRight, Check, Sparkles, Users, HelpCircle, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import Quiz from '../components/Quiz';
import ShopifyBuyButton from '../components/ShopifyBuyButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1
    }
  }
};

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuiz, setShowQuiz] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        
        toast.error('Failed to load products');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-28">
        <div className="spinner" />
      </div>
    );
  }

  const guide = products.find(p => p.id === 'signal-full-guide');
  const subscription = products.find(p => p.id === 'signal-accountability');

  return (
    <div className="pt-28 bg-black min-h-screen" data-testid="products-page">
      {/* Quiz Modal */}
      <AnimatePresence>
        {showQuiz && <Quiz onClose={() => setShowQuiz(false)} />}
      </AnimatePresence>

      {/* Hero Section */}
      <section className="py-20 md:py-28 border-b border-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
              The Signal: Upgrade
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-6 max-w-4xl">
              The Exact Glow-Up System I Used to Go From Invisible to High-Value
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-xl text-[#999999] mb-8 max-w-2xl">
              Stop guessing. This is the step-by-step system behind my transformation—everything I actually used.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 items-start">
              <p className="text-lg text-white font-medium">
                You've seen the results. This is exactly how I did it.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 md:py-20 bg-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
              The Problem
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-2xl md:text-3xl text-white mb-8">
              Most guys stay stuck because they...
            </motion.h2>
            <motion.div variants={staggerContainer} className="grid md:grid-cols-2 gap-4">
              {[
                "Try random products that don't work together",
                "Follow random TikTok advice with no real plan",
                "Don't know what actually makes a difference",
                "Stay stuck with no visible results"
              ].map((item, i) => (
                <motion.div 
                  key={i}
                  variants={fadeInUp}
                  className="flex items-start gap-3 bg-[#222222] p-4"
                >
                  <X className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                  <span className="text-[#CCCCCC]">{item}</span>
                </motion.div>
              ))}
            </motion.div>
            <motion.div variants={fadeInUp} className="mt-10 pt-8 border-t border-[#333333]">
              <p className="text-xl text-[#D4AF37] font-serif">
                "You don't need more tips. You need a system."
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Products */}
      <section className="py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
              What You Get
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl md:text-4xl text-white">
              Choose how you want to transform
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            {/* Guide */}
            {guide && (
              <motion.div 
                variants={fadeInUp}
                whileHover={{ y: -8 }}
                className="bg-[#0f0f0f] border border-[#1a1a1a] p-8 md:p-10"
                data-testid="product-section-signal-full-guide"
                id="product-section-signal-full-guide"
              >
                {/* Cover Image Preview */}
                <div className="mb-6 relative overflow-hidden border border-[#1a1a1a] group">
                  <img 
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/guide/cover`}
                    alt="The Signal: Upgrade - Guide Preview"
                    className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3">
                    <span className="text-[10px] uppercase tracking-widest text-[#D4AF37] bg-black/60 px-2 py-1">
                      58 pages
                    </span>
                  </div>
                </div>
                
                <p className="text-xs uppercase tracking-widest text-[#D4AF37] mb-2">
                  Complete Glow-Up System
                </p>
                <h2 className="font-serif text-2xl md:text-3xl text-white mb-2">
                  The Signal: Upgrade
                </h2>
                <p className="font-serif text-4xl text-white mb-2">
                  €{guide.price.toFixed(2)}
                </p>
                <p className="text-sm text-[#999999] mb-6">
                  One-time payment
                </p>
                <p className="text-[#999999] leading-relaxed mb-8">
                  The exact system I used. Every product, every routine, every step. No fluff.
                </p>

                {/* What's Inside */}
                <div className="space-y-3 mb-8">
                  {[
                    "Step-by-step glow-up system (what to do and when)",
                    "Hair improvement (fix flat/weak hair, style it right)",
                    "Skin routine (get clearer skin and a better glow)",
                    "Face improvement (reduce bloat, look sharper)",
                    "Body basics (look more defined overall)",
                    "Confidence & presence (eye contact, body language)",
                    "Lifetime access + future updates",
                    "Learn at your own pace"
                  ].map((benefit, i) => (
                    <motion.div 
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.05 }}
                      className="flex items-start gap-3"
                    >
                      <Check className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-white">{benefit}</span>
                    </motion.div>
                  ))}
                </div>

                <div className="space-y-3">
                  <ShopifyBuyButton productId="signal-full-guide" variant="default" />
                  <p className="text-[10px] text-[#666] text-center leading-relaxed">
                    By purchasing you agree to our{' '}
                    <Link to="/terms" className="text-[#D4AF37] hover:underline">terms</Link>
                    {' '}and waive your right of withdrawal as digital content is delivered instantly.
                  </p>
                </div>
              </motion.div>
            )}

            {/* Subscription */}
            {subscription && (
              <motion.div 
                variants={fadeInUp}
                whileHover={{ y: -8 }}
                className="bg-[#0f0f0f] border-2 border-[#D4AF37] p-8 md:p-10 relative"
                data-testid="product-section-signal-accountability"
              >
                <div className="absolute -top-3 right-6 bg-[#D4AF37] text-white px-4 py-1 text-xs uppercase tracking-widest font-semibold">
                  Personal Support
                </div>
                <div className="w-16 h-16 bg-[#0f0f0f] flex items-center justify-center mb-6">
                  <Users className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <p className="text-xs uppercase tracking-widest text-[#D4AF37] mb-2">
                  Guided Accountability
                </p>
                <h2 className="font-serif text-2xl md:text-3xl text-white mb-2">
                  1-on-1 Coaching
                </h2>
                <p className="font-serif text-4xl text-white mb-6">
                  €{subscription.price.toFixed(2)}
                  <span className="text-base text-[#999999] font-sans ml-2">/month</span>
                </p>
                <p className="text-[#999999] leading-relaxed mb-8">
                  For guys who want personal guidance and someone to keep them on track. Get direct feedback on your progress.
                </p>

                {/* Benefits */}
                <div className="space-y-3 mb-8">
                  {[
                    "Personal accountability partner",
                    "Weekly check-ins on your progress",
                    "Direct chat support when you need it",
                    "Customized advice for your situation",
                    "Access to private community",
                    "Motivation when you feel stuck",
                    "Cancel anytime"
                  ].map((benefit, i) => (
                    <motion.div 
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.05 }}
                      className="flex items-start gap-3"
                    >
                      <Check className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-white">{benefit}</span>
                    </motion.div>
                  ))}
                </div>

                <div className="space-y-3">
                  <Button 
                    disabled
                    className="w-full btn-premium bg-[#D4AF37]/50 text-white rounded-none py-4 text-sm uppercase tracking-widest font-semibold cursor-not-allowed"
                    data-testid="add-to-cart-signal-accountability"
                  >
                    Coming Soon
                  </Button>
                  <p className="text-xs text-[#999999] text-center">
                    Contact via TikTok for coaching inquiries
                  </p>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-16 md:py-20 bg-[#0f0f0f]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
              What To Expect
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl md:text-4xl text-white mb-10">
              After going through the system, you will...
            </motion.h2>
            <motion.div variants={staggerContainer} className="grid md:grid-cols-2 gap-6">
              {[
                "Look more attractive (it's not just confidence, you'll see real changes)",
                "Feel more confident in how you present yourself",
                "Stop being overlooked and start getting noticed",
                "Actually see visible improvements you can track"
              ].map((result, i) => (
                <motion.div 
                  key={i}
                  variants={fadeInUp}
                  className="flex items-start gap-4 bg-[#0f0f0f] p-6 text-left"
                >
                  <Check className="w-6 h-6 text-[#D4AF37] flex-shrink-0" />
                  <span className="text-white">{result}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Authority + Price Justification */}
      <section className="py-16 md:py-20 bg-[#1A1A1A]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.p variants={fadeInUp} className="text-[#999999] text-lg mb-6">
              Everything in this guide is based on what I actually used.
            </motion.p>
            <motion.p variants={fadeInUp} className="text-white text-2xl font-serif mb-8">
              You've already seen the results.
            </motion.p>
            <motion.div variants={fadeInUp} className="w-16 h-[1px] bg-[#D4AF37] mx-auto mb-8" />
            <motion.p variants={fadeInUp} className="text-[#999999] mb-10">
              You could spend months trying to figure this out yourself—testing random products, watching endless videos, making mistakes.
              <br /><br />
              <span className="text-white">Or just follow what already works.</span>
            </motion.p>
            <motion.div variants={fadeInUp} className="max-w-md mx-auto">
              <a href="#product-section-signal-full-guide">
                <Button 
                  className="w-full btn-premium bg-[#D4AF37] text-white hover:bg-[#C9A030] rounded-none py-4 text-sm uppercase tracking-widest font-semibold"
                >
                  Get The Signal: Upgrade Now
                </Button>
              </a>
              <p className="text-[#999999] text-sm mt-4">€19 <span className="text-[#D4AF37]">one-time</span></p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Comparison */}
      <section className="py-20 md:py-28 bg-black">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
              Quick Comparison
            </motion.p>
            <motion.h2 variants={fadeInUp} className="font-serif text-3xl md:text-4xl text-white">
              Which one is right for you?
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 gap-8"
          >
            {/* Guide Column */}
            <motion.div variants={fadeInUp} className="bg-[#0f0f0f] p-8 border border-[#1a1a1a]">
              <p className="text-[#D4AF37] text-xs uppercase tracking-widest mb-2">The Signal: Upgrade</p>
              <h3 className="font-serif text-2xl text-white mb-4">Best if you...</h3>
              <ul className="space-y-3 mb-6">
                {[
                  "Want to learn at your own pace",
                  "Are self-motivated and disciplined",
                  "Prefer a one-time investment",
                  "Like following step-by-step systems"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-[#999999]">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="font-serif text-3xl text-white">€19 <span className="text-sm text-[#999999] font-sans">one-time</span></p>
            </motion.div>

            {/* Subscription Column */}
            <motion.div variants={fadeInUp} className="bg-[#0f0f0f] p-8 border-2 border-[#D4AF37]">
              <p className="text-[#D4AF37] text-xs uppercase tracking-widest mb-2">Coaching</p>
              <h3 className="font-serif text-2xl text-white mb-4">Best if you...</h3>
              <ul className="space-y-3 mb-6">
                {[
                  "Want personal support and feedback",
                  "Need accountability to stay consistent",
                  "Have specific questions about your situation",
                  "Want to connect with others on the same journey"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-[#999999]">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="font-serif text-3xl text-[#D4AF37]">€200 <span className="text-sm text-[#999999] font-sans">/month</span></p>
            </motion.div>
          </motion.div>

          <motion.p 
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center text-[#999999] text-sm mt-8"
          >
            Get both? Save €10 when you bundle them together.
          </motion.p>
        </div>
      </section>

      {/* ============== COMING SOON - HAIR PRODUCTS ============== */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
            className="text-center"
          >
            <motion.p 
              variants={fadeInUp}
              className="text-sm uppercase tracking-[0.3em] text-[#D4AF37] mb-4"
            >
              Coming Soon
            </motion.p>
            <motion.h2 
              variants={fadeInUp}
              className="font-serif text-3xl md:text-4xl text-white mb-4"
            >
              Signal Hair Products
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-[#999999] max-w-xl mx-auto mb-8"
            >
              Premium hair products launching soon on a separate store.
            </motion.p>
            
            {/* Link button - disabled for now, will link to Shopify store later */}
            <motion.div variants={fadeInUp}>
              <Button 
                disabled
                className="bg-[#1a1a1a] text-[#666] border border-[#333] rounded-none px-8 py-3 text-sm uppercase tracking-widest cursor-not-allowed"
              >
                Coming Soon
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
